import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Copy, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import type { BotMeta, Message } from '@rundown/types';
import { formatDateTime, formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { MessageBodyView } from '../composer/MessageBodyView';
import { settleIntakeNow } from '../intake/api';
import { IntakeConversation } from '../intake/IntakeConversation';
import { ThinkingChecklist } from '../intake/PhaseIndicator';
import { intakeQueryKeys, useIntakeState } from '../intake/useIntakeState';
import type { IntakeState } from '../intake/types';
import { PRIORITY_LABELS, TYPE_LABELS, TYPE_TONES } from '../requests/labels';
import { threadQueryOptions } from './api';
import { ConfirmDelete, MessageActions } from './MessageControls';
import { ActionContextMenu, type MenuAction } from './ThreadMenu';
import { deleteMessage } from '../requests/messagesApi';
import { queryKeys } from '../../lib/queryKeys';
import { useCapability } from '../workspace/useCapability';

const TONE_CHIP: Record<string, string> = {
  neutral: 'bg-surface-3 text-muted',
  accent: 'bg-accent-tint text-accent',
  success: 'bg-success-tint text-success',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  violet: 'bg-violet-tint text-violet',
};

/** @rundown's avatar — a glyph, not an initial, so it never reads as a person. */
export function BotAvatar() {
  return (
    <div
      className="grid size-[1.625rem] flex-none place-items-center rounded-full bg-ink text-white"
      aria-hidden
    >
      <Bot size={14} strokeWidth={2} />
    </div>
  );
}

/**
 * A message @rundown wrote.
 *
 * The words are always there — they are what a notification, a snippet or an
 * old client shows — and `botMeta` adds what words cannot do: the question
 * card that can be answered in place, and chips for what was filed.
 */
export function BotMessage({ workspaceId, message }: { workspaceId: string; message: Message }) {
  const meta = message.botMeta;
  // @rundown's words are never edited — a person rewriting them would make
  // the bot appear to have said something it did not — but an admin can
  // remove one.
  const isAdmin = useCapability('workspace.admin');
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const remove = useMutation({
    mutationFn: () => deleteMessage(workspaceId, message.requestId, message.id),
    onSuccess: () =>
      void queryClient.invalidateQueries({
        queryKey: queryKeys.requestMessages(workspaceId, message.requestId),
      }),
  });

  const menu: MenuAction[] = [
    {
      key: 'copy',
      label: 'Copy text',
      icon: Copy,
      onSelect: () => void navigator.clipboard?.writeText(message.bodyText),
    },
    ...(isAdmin
      ? [
          {
            key: 'delete',
            label: 'Delete message',
            icon: Trash2,
            danger: true,
            separated: true,
            onSelect: () => setConfirming(true),
          },
        ]
      : []),
  ];

  return (
    <ActionContextMenu actions={menu}>
      <div className="group flex gap-2.5">
        <BotAvatar />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="flex-none fs-12 font-bold whitespace-nowrap">@rundown</span>
            <span className="fs-11 whitespace-nowrap text-muted">{formatDateTime(message.createdAt)}</span>
            {!message.visibleToClient && (
              <span className="rounded-full bg-violet-tint px-1.5 fs-10 font-semibold text-violet">
                Internal
              </span>
            )}
            {!confirming && (
              <MessageActions
                canEdit={false}
                canDelete={isAdmin}
                onEdit={() => undefined}
                onDelete={() => setConfirming(true)}
              />
            )}
          </div>
          {confirming && (
            <ConfirmDelete
              prompt="Delete this @rundown message for everyone?"
              pending={remove.isPending}
              error={remove.error}
              onConfirm={() => remove.mutate()}
              onCancel={() => setConfirming(false)}
            />
          )}

          {meta?.kind === 'filed' ? (
            <FiledCard meta={meta} />
          ) : (
            <MessageBodyView
              body={message.body}
              fallbackText={message.bodyText}
              resolveAttachmentUrl={() => undefined}
            />
          )}

          {meta?.kind === 'questions' && (
            <QuestionsCard workspaceId={workspaceId} requestId={message.requestId} round={meta.round} />
          )}
          {meta?.kind === 'answer' && meta.cited.length > 0 && (
            <ul className="mt-1.5 flex flex-wrap gap-1.5">
              {meta.cited.map((cited) => (
                <li key={cited.requestId}>
                  <Link
                    to={`/t/${cited.requestId}`}
                    className="inline-flex max-w-[22rem] items-baseline gap-1 rounded-full border border-line px-2 py-0.5 fs-11 text-ink-soft hover:bg-surface-2 hover:text-ink"
                  >
                    <span className="numeric flex-none text-muted">#{cited.number}</span>
                    <span className="truncate">{cited.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </ActionContextMenu>
  );
}

function FiledCard({ meta }: { meta: Extract<BotMeta, { kind: 'filed' }> }) {
  const chip = (tone: string, children: React.ReactNode) => (
    <span
      className={cx('rounded-full px-2 py-0.5 fs-11 font-semibold', TONE_CHIP[tone] ?? TONE_CHIP['neutral'])}
    >
      {children}
    </span>
  );
  return (
    <div className="mt-1 space-y-1.5">
      <p className="fs-13 text-ink">
        Filed as <span className="numeric font-semibold">#{meta.number}</span>. The team will review it.
      </p>
      <div className="flex flex-wrap items-center gap-1.5">
        {chip(TYPE_TONES[meta.type], TYPE_LABELS[meta.type])}
        {chip('neutral', `${PRIORITY_LABELS[meta.priority]} priority`)}
        {meta.approxMinutes > 0 && chip('neutral', `~${formatHours(meta.approxMinutes / 60)}`)}
        {meta.projectName !== null && chip('neutral', meta.projectName)}
      </div>
    </div>
  );
}

/**
 * The questions of one intake round, answerable in the thread.
 *
 * Live only while that round is the one waiting — a later round, or a filed
 * issue, collapses it to a line, so scrolling back through a thread never
 * offers a form that would answer the wrong round.
 */
function QuestionsCard({
  workspaceId,
  requestId,
  round,
}: {
  workspaceId: string;
  requestId: string;
  round: number;
}) {
  // Whether this thread is still being filed decides whether there can be a
  // live round at all — and the intake state (and its stream) is only worth
  // asking for when there can.
  const thread = useQuery(threadQueryOptions(workspaceId, requestId)).data;
  if (thread?.filing !== true) return <p className="mt-1 fs-11 text-faint">Answered.</p>;
  return <LiveQuestions workspaceId={workspaceId} requestId={requestId} round={round} />;
}

function LiveQuestions({
  workspaceId,
  requestId,
  round,
}: {
  workspaceId: string;
  requestId: string;
  round: number;
}) {
  const queryClient = useQueryClient();
  const query = useIntakeState(workspaceId, requestId);
  const state = query.data;

  const settle = useMutation({
    mutationFn: () => settleIntakeNow(workspaceId, requestId),
    onSuccess: (next: IntakeState) =>
      queryClient.setQueryData<IntakeState>(intakeQueryKeys.intake(workspaceId, requestId), next),
  });

  if (state === undefined) return null;
  const open = state.status === 'awaiting_answers' && state.round === round;
  if (!open) {
    return <p className="mt-1 fs-11 text-faint">Answered.</p>;
  }

  return (
    <div className="mt-2 rounded-[10px] border border-accent-border bg-accent-tint-soft px-3.5 py-3">
      <IntakeConversation
        workspaceId={workspaceId}
        requestId={requestId}
        state={state}
        showSummary={false}
        escape={
          <button
            type="button"
            onClick={() => settle.mutate()}
            disabled={settle.isPending}
            className="rounded-lg px-2.5 py-2 fs-12 font-semibold text-muted hover:text-ink disabled:opacity-50"
          >
            Post it as it is
          </button>
        }
      />
    </div>
  );
}

/**
 * "@rundown is working on it" — under the last message while a thread is
 * being converted and the model has not answered yet.
 */
export function BotThinking({ workspaceId, requestId }: { workspaceId: string; requestId: string }) {
  const thread = useQuery(threadQueryOptions(workspaceId, requestId)).data;
  if (thread?.filing !== true) return null;
  return <FilingIndicator workspaceId={workspaceId} requestId={requestId} />;
}

function FilingIndicator({ workspaceId, requestId }: { workspaceId: string; requestId: string }) {
  const state = useIntakeState(workspaceId, requestId).data;
  if (state?.status !== 'thinking') return null;
  return (
    <div className="flex gap-2.5">
      <BotAvatar />
      <div className="min-w-0 flex-1">
        <div className="mb-1 fs-12 font-bold">@rundown</div>
        <ThinkingChecklist
          phases={state.phases}
          step={state.phaseStep}
          fallback={state.phase ?? 'Reading the thread…'}
        />
      </div>
    </div>
  );
}
